/**
 * Device Token Storage Utility
 *
 * Manages multiple device tokens in localStorage for different Blitz accounts.
 * Supports migration from single-token to multi-token storage.
 */

const STORAGE_KEY = 'blitz_device_tokens';
const LEGACY_KEY = 'blitz_device_token';
const EMAIL_MAPPING_KEY = 'blitz_email_to_identifier_map';
const MAX_TOKENS = 10; // Prevent unbounded growth

/**
 * Create a unique identifier for a user account
 * @param {Object} params - User identifiers
 * @param {number} [params.user_id] - Regular user ID
 * @param {string} [params.creator_username] - Creator username
 * @param {number} [params.client_id] - Client ID
 * @returns {string|null} Unique identifier or null if invalid params
 */
export function createUserIdentifier({ user_id, creator_username, client_id }) {
  if (user_id) return `user_${user_id}`;
  if (creator_username) return `creator_${creator_username}`;
  if (client_id) return `client_${client_id}`;
  return null;
}

/**
 * Load all device tokens from localStorage
 * @returns {Object} Map of user identifiers to device tokens
 */
export function loadDeviceTokens() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate it's an object
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('[DeviceToken] Failed to parse device tokens:', err);
  }
  return {};
}

/**
 * Save device tokens to localStorage
 * @param {Object} tokensObject - Map of user identifiers to device tokens
 */
export function saveDeviceTokens(tokensObject) {
  try {
    // Validate input
    if (typeof tokensObject !== 'object' || tokensObject === null || Array.isArray(tokensObject)) {
      console.error('[DeviceToken] Invalid tokens object:', tokensObject);
      return;
    }

    // Enforce max tokens limit (keep most recently used)
    const entries = Object.entries(tokensObject);
    if (entries.length > MAX_TOKENS) {
      console.warn(`[DeviceToken] Token limit exceeded (${entries.length}/${MAX_TOKENS}), keeping most recent`);
      // For simplicity, keep first MAX_TOKENS entries
      // In production, could track last-used timestamps
      const limited = Object.fromEntries(entries.slice(0, MAX_TOKENS));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokensObject));
    }
  } catch (err) {
    console.error('[DeviceToken] Failed to save device tokens:', err);
  }
}

/**
 * Get device token for a specific user
 * @param {Object} params - User identifiers
 * @param {number} [params.user_id] - Regular user ID
 * @param {string} [params.creator_username] - Creator username
 * @param {number} [params.client_id] - Client ID
 * @returns {string|null} Device token or null if not found
 */
export function getDeviceToken({ user_id, creator_username, client_id }) {
  const identifier = createUserIdentifier({ user_id, creator_username, client_id });
  if (!identifier) return null;

  const tokens = loadDeviceTokens();
  return tokens[identifier] || null;
}

/**
 * Store device token for a specific user
 * @param {Object} params - User identifiers
 * @param {number} [params.user_id] - Regular user ID
 * @param {string} [params.creator_username] - Creator username
 * @param {number} [params.client_id] - Client ID
 * @param {string} deviceToken - The device token to store
 */
export function setDeviceToken({ user_id, creator_username, client_id }, deviceToken) {
  console.log('[DeviceToken] setDeviceToken called:', {
    user_id,
    creator_username,
    client_id,
    deviceToken: deviceToken ? `${deviceToken.substring(0, 10)}...` : 'missing'
  });

  const identifier = createUserIdentifier({ user_id, creator_username, client_id });

  if (!identifier) {
    console.error('[DeviceToken] FAILED: Cannot create identifier - all values are null/undefined', {
      user_id: user_id || 'null',
      creator_username: creator_username || 'null',
      client_id: client_id || 'null',
      received_token: !!deviceToken
    });
    console.error('[DeviceToken] TwoFactorModal did not receive user identifiers from parent');
    return;
  }

  const tokens = loadDeviceTokens();

  // Debug: Log if we're overwriting an existing token
  if (tokens[identifier]) {
    console.log(`[DeviceToken] Updating existing token for ${identifier}`);
  } else {
    console.log(`[DeviceToken] Storing new token for ${identifier}`);
  }

  tokens[identifier] = deviceToken;
  saveDeviceTokens(tokens);

  console.info(`[DeviceToken] Successfully stored token for ${identifier}`);
  console.log('[DeviceToken] DEBUG - All tokens after update:', Object.keys(tokens));
}

/**
 * Remove device token for a specific user
 * @param {Object} params - User identifiers
 * @param {number} [params.user_id] - Regular user ID
 * @param {string} [params.creator_username] - Creator username
 * @param {number} [params.client_id] - Client ID
 */
export function removeDeviceToken({ user_id, creator_username, client_id }) {
  const identifier = createUserIdentifier({ user_id, creator_username, client_id });
  if (!identifier) return;

  const tokens = loadDeviceTokens();
  delete tokens[identifier];
  saveDeviceTokens(tokens);

  console.info(`[DeviceToken] Removed token for ${identifier}`);
}

/**
 * Get legacy single device token (for migration)
 * @returns {string|null} Legacy device token or null
 */
export function getLegacyDeviceToken() {
  return localStorage.getItem(LEGACY_KEY);
}

/**
 * Migrate legacy single token to new multi-token storage
 * This is called during login to automatically migrate existing users
 */
export function migrateLegacyToken() {
  const legacyToken = getLegacyDeviceToken();
  if (!legacyToken) return;

  const tokens = loadDeviceTokens();

  // Check if we've already migrated (any tokens exist)
  if (Object.keys(tokens).length > 0) {
    console.info('[DeviceToken] Migration already completed, skipping');
    return;
  }

  // Store legacy token with a special identifier
  // Backend will validate it and if valid, we'll re-store with correct identifier
  tokens['legacy_migrated'] = legacyToken;
  saveDeviceTokens(tokens);

  console.info('[DeviceToken] Migrated legacy token to new storage');
}

/**
 * Load email to identifier mapping
 */
function loadEmailToIdentifierMapping() {
  try {
    const stored = localStorage.getItem(EMAIL_MAPPING_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('[DeviceToken] Failed to parse email mapping:', err);
  }
  return {};
}

/**
 * Save email to identifier mapping
 */
function saveEmailToIdentifierMapping(mapping) {
  try {
    if (typeof mapping !== 'object' || mapping === null || Array.isArray(mapping)) {
      console.error('[DeviceToken] Invalid email mapping:', mapping);
      return;
    }
    localStorage.setItem(EMAIL_MAPPING_KEY, JSON.stringify(mapping));
  } catch (err) {
    console.error('[DeviceToken] Failed to save email mapping:', err);
  }
}

/**
 * Map email to identifier for future lookups
 */
export function mapEmailToIdentifier(email, { user_id, creator_username, client_id }) {
  const identifier = createUserIdentifier({ user_id, creator_username, client_id });

  if (!identifier || !email) {
    console.warn('[DeviceToken] Cannot create email mapping - missing data', { email, user_id, creator_username, client_id });
    return;
  }

  const mapping = loadEmailToIdentifierMapping();
  const emailKey = email.toLowerCase();

  // Debug: Log if we're overwriting an existing mapping
  if (mapping[emailKey] && mapping[emailKey] !== identifier) {
    console.warn(`[DeviceToken] WARNING: Overwriting mapping for ${emailKey}: ${mapping[emailKey]} → ${identifier}`);
  }

  mapping[emailKey] = identifier;
  saveEmailToIdentifierMapping(mapping);

  console.info(`[DeviceToken] Mapped ${emailKey} → ${identifier}`);
  console.log('[DeviceToken] DEBUG - All mappings after update:', mapping);
}

/**
 * Get device token by email (solves multi-account bug)
 */
export function getDeviceTokenByEmail(email) {
  if (!email) {
    console.warn('[DeviceToken] getDeviceTokenByEmail called without email');
    return null;
  }

  const emailKey = email.toLowerCase();
  const mapping = loadEmailToIdentifierMapping();
  const tokens = loadDeviceTokens();

  // Debug: Log current state
  console.log('[DeviceToken] DEBUG - Current storage state:', {
    email: emailKey,
    allMappings: mapping,
    allTokenIdentifiers: Object.keys(tokens)
  });

  const identifier = mapping[emailKey];

  if (!identifier) {
    console.info(`[DeviceToken] No identifier mapping for ${emailKey}`);
    return null;
  }

  const token = tokens[identifier];

  if (token) {
    console.info(`[DeviceToken] Found token for ${emailKey} (identifier: ${identifier})`);
  } else {
    console.warn(`[DeviceToken] Identifier found for ${emailKey}, but no token stored`);
    console.warn('[DeviceToken] DEBUG - Available tokens:', Object.keys(tokens));
  }

  return token || null;
}

/**
 * Get fallback token - checks new storage first, then legacy, then attempts migration
 * Use this during login when we don't yet know the user's specific identifier
 * @returns {string|null} Device token or null
 */
export function getFallbackDeviceToken() {
  console.warn('[DeviceToken] getFallbackDeviceToken is DEPRECATED - may return wrong token');
  console.warn('[DeviceToken] Use getDeviceTokenByEmail() instead');

  // Keep for backwards compatibility
  const tokens = loadDeviceTokens();
  if (tokens['legacy_migrated']) {
    return tokens['legacy_migrated'];
  }

  const legacyToken = getLegacyDeviceToken();
  if (legacyToken) {
    migrateLegacyToken();
    return legacyToken;
  }

  return null;
}

/**
 * Clear all device tokens (logout all accounts)
 */
export function clearAllDeviceTokens() {
  localStorage.removeItem(STORAGE_KEY);
  console.info('[DeviceToken] Cleared all device tokens');
}

/**
 * Clean up expired or invalid tokens
 * This should be called periodically or after failed auth attempts
 * @param {Array<string>} invalidIdentifiers - List of identifiers to remove
 */
export function cleanupDeviceTokens(invalidIdentifiers = []) {
  const tokens = loadDeviceTokens();
  let cleaned = false;

  invalidIdentifiers.forEach(identifier => {
    if (tokens[identifier]) {
      delete tokens[identifier];
      cleaned = true;
    }
  });

  if (cleaned) {
    saveDeviceTokens(tokens);
    console.info(`[DeviceToken] Cleaned up ${invalidIdentifiers.length} invalid tokens`);
  }
}
